import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ConfigProvider, theme as antdThemes } from 'antd'
import { antdTheme } from './index'
import { USER_CACHE_EVENT, getUserCachedData, updateUserCachedData } from 'utils/UserAuthorization'
import { salvarMeusDados } from 'services/perfil'

// Modo claro/escuro. O antd troca sozinho (algorithm); o resto do sistema usa as
// variáveis --yrm-* definidas em assets/global.js para [data-tema="escuro"].
//
// O padrão é sempre claro. O escuro vale só para quem escolher (sol/lua do topo
// ou Meu perfil), e a escolha fica salva nas preferências do usuário no banco,
// valendo em qualquer computador. Telas públicas (portal do cliente, login,
// cadastro) ficam sempre no claro da marca.
const CHAVE = 'yrm:tema'
const TemaContext = createContext({ escuro: false, alternar: () => {}, fixarClaro: () => {} })

const temaDoUsuario = () => getUserCachedData()?.preferencias?.tema
const inicial = () => {
  const doUsuario = temaDoUsuario()
  if (doUsuario === 'escuro' || doUsuario === 'claro') return doUsuario === 'escuro'
  try {
    // mesmo navegador antes do login (ou sessão antiga sem a preferência no banco)
    return localStorage.getItem(CHAVE) === 'escuro'
  } catch { return false }
}

// Tokens do antd no escuro: azul-marinho da marca nas superfícies e dourado (o do logo
// escurecido), com texto escuro dentro dos botões principais para dar contraste
const ESCURO = {
  colorPrimary: '#d6a93a',
  colorLink: '#e6c46a',
  colorBgBase: '#0e1029',
  colorBgContainer: '#15173a',
  colorBgElevated: '#1a1d45',
  colorBorder: '#33376b',
  colorBorderSecondary: '#2a2d5c',
  colorSplit: 'rgba(255, 255, 255, 0.07)',
  colorText: '#e8e9f3',
  colorTextSecondary: '#9a9dbd',
  colorTextPlaceholder: '#7a7ea3',
}

// Marca o <html> na hora (antes de redesenhar): quem lê as cores ao desenhar
// (gráficos em canvas) já pega as do tema novo
function aplicar(escuro) {
  document.documentElement.dataset.tema = escuro ? 'escuro' : 'claro'
  document.documentElement.style.colorScheme = escuro ? 'dark' : 'light'
}

// Guarda a escolha no banco (preferências do usuário) e no cache local
function salvarEscolha(escuro) {
  const tema = escuro ? 'escuro' : 'claro'
  try { localStorage.setItem(CHAVE, tema) } catch { /* ok */ }
  const usuario = getUserCachedData()
  if (!usuario?.token) return
  updateUserCachedData({ preferencias: { ...(usuario.preferencias || {}), tema } })
  salvarMeusDados({ preferencias: { tema } }).catch(() => { /* fica o local; tenta de novo na próxima troca */ })
}

export function TemaProvider({ children }) {
  const [escolhido, setEscolhido] = useState(inicial)
  // telas públicas pedem o claro da marca sem mexer na escolha da pessoa
  const [claroFixo, setClaroFixo] = useState(false)
  const escuro = escolhido && !claroFixo

  useEffect(() => { aplicar(escuro) }, [escuro])

  // login (ou /me) trouxe a preferência salva no banco: passa a valer
  useEffect(() => {
    const doBanco = () => {
      const t = temaDoUsuario()
      if (t === 'escuro' || t === 'claro') setEscolhido(t === 'escuro')
    }
    window.addEventListener(USER_CACHE_EVENT, doBanco)
    return () => window.removeEventListener(USER_CACHE_EVENT, doBanco)
  }, [])

  const alternar = useCallback(() => setEscolhido((v) => { aplicar(!v); salvarEscolha(!v); return !v }), [])
  const fixarClaro = useCallback((sim) => setClaroFixo(Boolean(sim)), [])
  const valor = useMemo(() => ({ escuro, alternar, fixarClaro }), [escuro, alternar, fixarClaro])
  const tema = useMemo(() => (escuro
    ? {
      ...antdTheme,
      algorithm: antdThemes.darkAlgorithm,
      token: { ...antdTheme.token, ...ESCURO },
      components: { ...antdTheme.components, Button: { ...antdTheme.components.Button, primaryColor: '#13143A' } },
    }
    : antdTheme), [escuro])

  return (
    <TemaContext.Provider value={valor}>
      <ConfigProvider theme={tema}>{children}</ConfigProvider>
    </TemaContext.Provider>
  )
}

export const useTema = () => useContext(TemaContext)

// Usado pelo roteador: nas telas públicas o tema fica no claro da marca
export function TemaFixoClaro({ ativo }) {
  const { fixarClaro } = useTema()
  useEffect(() => {
    fixarClaro(ativo)
    return () => fixarClaro(false)
  }, [ativo, fixarClaro])
  return null
}
